// Standard library imports
import React, { useEffect, useState } from 'react';

// External library imports
import { toast } from 'react-toastify';
import { Form, Formik } from 'formik';
import { UilTimesCircle, UilFileDownload, UilPlusCircle } from '@iconscout/react-unicons';
import { Accordion, AccordionItem, AccordionItemHeading, AccordionItemButton, AccordionItemPanel } from 'react-accessible-accordion';

// Internal module import
import { DocumentsService } from '../../../../services/DocumentService';
import CkeEditor from '../../../../components/CkeEditor';
import { useLoader } from '../../../../hooks';
import Typography from '../../../../components/Typography/Typography';
import MultipleFilesUpload from '../../../../components/Inputs/FileUpload/MultipleFileUpload';
import DeleteModal from '../../../../components/DeleteModal/DeleteModal';
import InputComponent from '../../../Edge/ModalComponent/InputComponent';

// Css imports
import edgeClasses from '../../../Edge/Edge.module.css';
import classes from '../../../../styles/AllDevices.module.css';

const DocumentEditor = ({ customerData, setOpen }) => {
    const [documentData, setDocumentData] = useState('');
    const [startLoader, stopLoader] = useLoader();
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [storedDocumentData, setStoredDocumentData] = useState('');
    const [confirmationModal, setConfirmationModal] = useState(false);
    const [activePageId, setActivePageId] = useState(1);
    const [readOnly, setReadOnly] = useState(true);
    const [documentsPage, setDocumentsPage] = useState([{ name: 'Page 1', pageId: 1, data: '' }]);

    useEffect(() => {
        const data = documentsPage.find((item) => item.pageId === activePageId);
        setDocumentData(data.data);
    }, [documentsPage, activePageId]);

    useEffect(() => {
        if (customerData?._id) {
            getDocument();
        }
    }, [customerData]);

    const getDocument = () => {
        DocumentsService.GetDocument(customerData._id, startLoader, handleGetDocumentSuccess, handleError, stopLoader);
    };

    const handleGetDocumentSuccess = ({ data }) => {
        data?.data?.pages?.length && setDocumentsPage(data?.data?.pages);
        data?.data?.pages?.length && setStoredDocumentData(data?.data?.pages);
        setUploadedFiles(data?.data?.files || []);
    };

    const saveDocument = () => {
        const payload = {
            id: customerData._id,
            pages: documentsPage,
        };
        DocumentsService.SaveDocument(payload, startLoader, handleSaveDocumentSuccess, handleError, stopLoader);
    };

    const uploadFiles = (files) => {
        let tempFiles = [];
        for (let i = 0; i < files.length; i++) {
            tempFiles.push(files[i]);
        }
        setUploadedFiles(uploadedFiles.concat(tempFiles));
        const formData = new FormData();
        formData.append('id', customerData._id);
        for (let i = 0; i < files.length; i++) {
            formData.append('files', files[i]);
        }
        DocumentsService.UploadFiles(formData, handleUploadFilesSuccess, handleError);
    };

    const handleUploadFilesSuccess = () => {
        getUploadedFiles();
    };

    const getUploadedFiles = () => {
        DocumentsService.GetFiles(customerData._id, handleGetUploadedFilesSuccess, handleError);
    };

    const handleGetUploadedFilesSuccess = ({ data }) => {
        setUploadedFiles(data.data?.files || []);
    };
    const handleSaveDocumentSuccess = () => {
        toast.success('Document saved!');
        setOpen(false);
        setDocumentData('');
    };

    const handleError = (err) => {
        console.log(err);
        if (err && err.response) toast.error(err.response.data.message);
    };

    const handleUploadClick = (e) => {
        const files = e.target.files;
        uploadFiles(files);
    };

    const removeFile = (key) => {
        const payload = {
            id: customerData._id,
            file: key,
        };
        DocumentsService.RemoveFile(payload, startLoader, handleRemoveFilesSuccess, handleError, stopLoader);
    };

    const handleRemoveFilesSuccess = () => {
        toast.success('File Removed!');
        getUploadedFiles();
    };

    const addPage = () => {
        setDocumentsPage((prevData) => [
            ...prevData,
            {
                name: `Page ${prevData.length + 1}`,
                pageId: prevData.length + 1,
                data: '',
            },
        ]);
        setActivePageId(documentsPage.length + 1);
    };

    const setPageDocumentData = (data) => {
        const temp = documentsPage.map((item) => {
            if (item.pageId === activePageId) {
                return { ...item, data: data };
            } else {
                return item;
            }
        });
        setDocumentsPage(temp);
    };

    return (
        <div className={edgeClasses.DocumentContainer}>
            <DeleteModal
                opendeleteModal={confirmationModal}
                setOpenDeleteModal={(e) => {
                    setConfirmationModal(e);
                    setOpen(false);
                }}
                deletefunction={saveDocument}
                text={'Changes you made may not be saved.'}
                yesButtonText={'Save and Close'}
                noButtonText={'Close'}
            />
            <Formik enableReinitialize initialValues={{ editor: {} }}>
                {({ errors, touched, values, isValidating, ...props }) => (
                    <Form>
                        <div className={edgeClasses.HeaderSection}>
                            <Typography content={customerData?.name ? customerData?.name : customerData?._id} />
                            <div className={classes.ButtonContainer} style={{ marginTop: '0' }}>
                                <div>
                                    <button
                                        className="btn-secondary"
                                        style={{ marginTop: '0' }}
                                        onClick={() => {
                                            let isEdited = false;
                                            if (storedDocumentData.length !== documentsPage.length) {
                                                isEdited = true;
                                            } else {
                                                for (let i = 0; i < storedDocumentData.length; i++) {
                                                    if (storedDocumentData[i].name !== documentsPage[i].name || storedDocumentData[i].data !== documentsPage[i].data) {
                                                        isEdited = true;
                                                        break;
                                                    }
                                                }
                                            }

                                            if (!isEdited || readOnly) {
                                                setOpen(false);
                                                setDocumentData('');
                                            } else {
                                                setConfirmationModal(true);
                                            }
                                        }}
                                    >
                                        Close
                                    </button>
                                </div>
                                <div>
                                    {readOnly ? (
                                        <button type="button" style={{ marginTop: '0' }} className="btn-primary" onClick={() => setReadOnly(false)}>
                                            Edit
                                        </button>
                                    ) : (
                                        <button type="submit" style={{ marginTop: '0' }} className="btn-primary" onClick={saveDocument}>
                                            Save
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div style={{ marginBottom: '1vh' }}>
                            <Accordion allowZeroExpanded>
                                <AccordionItem>
                                    <AccordionItemHeading>
                                        <AccordionItemButton>
                                            <div>Documents</div>
                                        </AccordionItemButton>
                                    </AccordionItemHeading>
                                    <AccordionItemPanel>
                                        <div className={edgeClasses.UploadSection}>
                                            {!readOnly && (
                                                <div className={classes.FieldControl2} style={{ width: '25vw' }}>
                                                    <label for="uploadFiles">Upload files</label>
                                                    <MultipleFilesUpload name="uploadFiles" id="uploadFiles" accept="*" multiple onChange={handleUploadClick} disabled={readOnly} />
                                                </div>
                                            )}

                                            <div className={classes.FieldControl2}>
                                                {uploadedFiles?.length ? <label for="uploadedFiles">Uploaded files</label> : ''}
                                                <div className={edgeClasses.UploadedFiles}>
                                                    {uploadedFiles?.length
                                                        ? uploadedFiles.map((item, index) => (
                                                            <div>
                                                                <div className={edgeClasses.SelectedFile}>
                                                                    <div key={index}>
                                                                        <p>{item.name}</p>
                                                                    </div>
                                                                    {item?.key ? (
                                                                        <div style={{ display: 'flex' }}>
                                                                            <div className={edgeClasses.CloseIcon}>
                                                                                <a href={item.location} download={item.name} target="_blank">
                                                                                    <UilFileDownload
                                                                                        style={{
                                                                                            color: 'var(--color-primary)',
                                                                                        }}
                                                                                        size={'1.3vw'}
                                                                                    />
                                                                                </a>
                                                                            </div>
                                                                            {!readOnly && (
                                                                                <div onClick={() => removeFile(item.key)} className={edgeClasses.CloseIcon}>
                                                                                    <UilTimesCircle
                                                                                        style={{
                                                                                            color: 'var(--color-primary)',
                                                                                        }}
                                                                                        size={'1.3vw'}
                                                                                    />
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    ) : (
                                                                        ''
                                                                    )}
                                                                </div>
                                                                {!item?.key ? <p className={edgeClasses.Uploading}>Uploading...</p> : ''}
                                                            </div>
                                                        ))
                                                        : ''}
                                                </div>
                                            </div>
                                        </div>
                                    </AccordionItemPanel>
                                </AccordionItem>
                            </Accordion>
                        </div>
                        <div>
                            <CkeEditor
                                name="editor"
                                onChange={(e) => setPageDocumentData(e)}
                                defaultValue={documentData}
                                apiUrl={'admin/documents/upload-image'}
                                disabled={readOnly}
                            ></CkeEditor>
                        </div>
                        <div className={edgeClasses.PageContainer}>
                            {documentsPage?.map((item) => {
                                if (item?.name) {
                                    return (
                                        <InputComponent
                                            pageValue={item}
                                            setDocumentsPage={setDocumentsPage}
                                            setDocumentData={setDocumentData}
                                            setActivePageId={setActivePageId}
                                            activePageId={activePageId}
                                            documentsPage={documentsPage}
                                        />
                                    );
                                }
                            })}
                            {!readOnly && (
                                <UilPlusCircle
                                    size={'1.2vw'}
                                    style={{
                                        color: 'var(--color-primary)',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => {
                                        addPage();
                                    }}
                                />
                            )}
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default DocumentEditor;
